import { graphHooks } from '@tunasong/graph-lib/react'
import { profilesApi, skipToken, useSelector } from '@tunasong/redux'
import { performancePartLabels } from '@tunasong/schemas'
import type { FC } from 'react'
import { useNavigate } from 'react-router'
import { usePresence } from '../hooks/presence.js'
import { Button } from '../primitives/button.js'
import { Skeleton } from '../primitives/skeleton.js'
import { useProfileCardContext } from './profile-card-context.js'
import { ProfileAvatarImage } from './profile-avatar-image.js'
import styles from './profile-hover-card.module.css'

export interface ProfileHoverCardProps {
  userId: string
}

/** The content of the card that opens from a user's avatar: who they are, what they play in this band, whether they are here */
export const ProfileHoverCard: FC<ProfileHoverCardProps> = ({ userId }) => {
  const loggedInId = useSelector(state => state.user.userId)
  const { profile } = graphHooks.useProfile(userId)
  const { entityId, band } = useProfileCardContext()
  const presentIds = usePresence(entityId)
  const { data: bandParts, isLoading: partsLoading } = profilesApi.useLoadBandPartsQuery(
    band ? { bandId: band.id } : skipToken
  )
  const navigate = useNavigate()

  const isSelf = userId === loggedInId
  const isOwner = Boolean(band?.ownerId && band.ownerId === userId)
  const hereNow = !isSelf && presentIds.includes(userId)
  const parts = bandParts?.parts[userId] ?? []
  const name = profile?.name ?? profile?.email ?? 'Unknown user'

  const handleEditProfile = () => void navigate('/app/profile')

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.avatar}>
          {profile ? <ProfileAvatarImage profile={profile} px={56} alt="" /> : null}
          {hereNow ? <span className={styles.presenceDot} aria-hidden="true" /> : null}
        </div>
        <div className={styles.identity}>
          <div className={styles.nameRow}>
            <span className={styles.name}>{name}</span>
            {isSelf ? <span className={styles.secondary}>(you)</span> : null}
            {isOwner ? <span className={styles.badge}>Owner</span> : null}
          </div>
          {band && partsLoading ? (
            <div className={styles.parts} aria-label="Loading parts">
              <Skeleton variant="rounded" width={56} height={20} />
              <Skeleton variant="rounded" width={64} height={20} />
            </div>
          ) : null}
          {band && parts.length > 0 ? (
            <ul className={styles.parts} aria-label={`Plays in ${band.name}`}>
              {parts.map(part => (
                <li key={part} className={styles.part}>
                  {performancePartLabels[part]}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
      {hereNow ? (
        <div className={styles.presence}>
          <span className={styles.presenceInline} aria-hidden="true" />
          Here now
        </div>
      ) : null}
      {isSelf ? (
        <Button variant="contained" fullWidth onClick={handleEditProfile}>
          Edit profile
        </Button>
      ) : null}
    </div>
  )
}
